/**
 * Promise helpers for calls that can stall rather than fail.
 *
 * A rejected promise is easy — every caller already has a catch. The dangerous case is
 * the one that never settles at all: the Firestore SDK on a phone that is nominally
 * online but passing no data queues the operation instead of erroring, so an `await`
 * on it simply never returns. Anything latched before that await stays latched forever.
 */

/**
 * Thrown when an operation exceeds its budget. Carries the operation name so callers
 * can tell a stall apart from a genuine failure and say something useful about it.
 */
export class TimeoutError extends Error {
  constructor(message, { operation, timeoutMs } = {}) {
    super(message);
    this.name = 'TimeoutError';
    this.operation = operation;
    this.timeoutMs = timeoutMs;
  }
}

/**
 * Give a promise a deadline, converting a stall into a rejection the caller can handle.
 *
 * The underlying promise is not cancelled — nothing in the Firestore SDK supports that.
 * It is abandoned: if it settles later, its result is ignored. That is deliberate, and
 * safe here, because every caller's recovery path puts the user back in control rather
 * than depending on the abandoned work.
 *
 * @param {Promise<T>} promise - The operation to bound.
 * @param {number} timeoutMs - How long it is allowed to take.
 * @param {string} operation - Name used in the error message and telemetry.
 * @returns {Promise<T>} Resolves with the promise's value, or rejects with TimeoutError.
 * @template T
 */
export function withTimeout(promise, timeoutMs, operation) {
  let timer;

  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new TimeoutError(
        `${operation} did not complete within ${timeoutMs}ms`,
        { operation, timeoutMs }
      ));
    }, timeoutMs);
  });

  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}
